import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, AlertTriangle } from "lucide-react";
import {
  useProcedures,
  useUpdateProcedure,
  PROCEDURE_STATUS_LABEL,
  ProcedureStatus,
  procedureClientName,
  isProcedureOverdue,
} from "@/api/procedures";
import { useAuthStore } from "@/stores/authStore";
import toast from "react-hot-toast";
import { getApiErrorMessage } from "@/api/client";
import CreateProcedureModal from "./CreateProcedureModal";

const STATUS_STYLE: Record<ProcedureStatus, string> = {
  PENDIENTE: "bg-gray-100 text-gray-600",
  EN_PROCESO: "bg-blue-100 text-blue-700",
  EN_REVISION: "bg-purple-100 text-purple-700",
  APROBADO: "bg-teal-100 text-teal-700",
  FINALIZADO: "bg-green-100 text-green-700",
  CANCELADO: "bg-gray-100 text-gray-400",
};

const STATUS_OPTIONS = Object.keys(PROCEDURE_STATUS_LABEL) as ProcedureStatus[];

export default function ProceduresPage() {
  const [status, setStatus] = useState<ProcedureStatus | "">("");
  const [search, setSearch] = useState("");
  const [overdue, setOverdue] = useState(false);
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const hasPermission = useAuthStore((s) => s.hasPermission);

  const { data, isLoading } = useProcedures({
    status: status || undefined,
    search: search || undefined,
    overdue: overdue || undefined,
    page,
    pageSize: 15,
  });
  const updateProcedure = useUpdateProcedure();

  const procedures = data?.data ?? [];
  const meta = data?.meta;

  const handleStatusChange = async (id: string, newStatus: ProcedureStatus) => {
    try {
      await updateProcedure.mutateAsync({ id, status: newStatus });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Trámites</h1>
          <p className="text-sm text-gray-500">Procesos ante entidades externas (Cámara de Comercio, DIAN, etc.) por cliente.</p>
        </div>
        {hasPermission("procedures.create") && (
          <button type="button" className="btn-primary" onClick={() => setShowCreate(true)}>
            <Plus size={16} className="mr-1.5" /> Nuevo trámite
          </button>
        )}
      </div>

      <div className="card flex flex-wrap items-center gap-3 p-4">
        <input
          type="search"
          placeholder="Buscar por título..."
          className="input-field min-w-[220px] flex-1"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <select
          className="input-field w-auto"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as ProcedureStatus | "");
            setPage(1);
          }}
        >
          <option value="">Todos los estados</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {PROCEDURE_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={overdue}
            onChange={(e) => {
              setOverdue(e.target.checked);
              setPage(1);
            }}
          />
          Solo vencidos
        </label>
      </div>

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Trámite</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Cliente</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Entidad</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Vence</th>
              <th className="px-4 py-3 text-left font-medium text-gray-500">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-gray-400">
                  Cargando...
                </td>
              </tr>
            )}
            {!isLoading && procedures.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-gray-400">
                  No hay trámites que coincidan con los filtros.
                </td>
              </tr>
            )}
            {procedures.map((procedure) => (
              <tr key={procedure.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <p className="font-medium text-gray-900">{procedure.title}</p>
                  {procedure.procedureType && <p className="text-xs text-gray-400">{procedure.procedureType}</p>}
                </td>
                <td className="px-4 py-3 text-gray-600">
                  {procedure.client ? (
                    <Link to={`/clients/${procedure.client.id}`} className="hover:text-brand-600">
                      {procedureClientName(procedure)}
                    </Link>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-gray-600">{procedure.entityName || "—"}</td>
                <td className="px-4 py-3">
                  {procedure.dueDate ? (
                    <span className={isProcedureOverdue(procedure) ? "inline-flex items-center gap-1 text-red-600" : "text-gray-600"}>
                      {isProcedureOverdue(procedure) && <AlertTriangle size={12} />}
                      {procedure.dueDate}
                    </span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {hasPermission("procedures.update") ? (
                    <select
                      className={`rounded-full border-0 px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[procedure.status]}`}
                      value={procedure.status}
                      onChange={(e) => handleStatusChange(procedure.id, e.target.value as ProcedureStatus)}
                    >
                      {STATUS_OPTIONS.map((s) => (
                        <option key={s} value={s}>
                          {PROCEDURE_STATUS_LABEL[s]}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[procedure.status]}`}>
                      {PROCEDURE_STATUS_LABEL[procedure.status]}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {meta && meta.totalPages && meta.totalPages > 1 && (
          <div className="flex items-center justify-between border-t border-gray-100 px-4 py-3 text-sm">
            <span className="text-gray-500">
              Página {meta.page} de {meta.totalPages} ({meta.total} trámites)
            </span>
            <div className="space-x-2">
              <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                Anterior
              </button>
              <button
                className="btn-secondary"
                disabled={!!meta.totalPages && page >= meta.totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {showCreate && <CreateProcedureModal onClose={() => setShowCreate(false)} />}
    </div>
  );
}
